import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react';
import {AccountFields} from '../../shared/fields/keyplay';
import {EnrichedFields} from '../../shared/fields/enrichment';
import {IntentIcon} from '../Icons/Intent';
import {useIntentFields} from './useIntentFields';

// Renderer for Intent Score tier (without popover)
const IntentTierRenderer = ({
  tier,
  score,
}: {
  tier?: 'high' | 'medium' | 'low';
  score?: number;
}) => {
  return (
    <Flex alignItems="center" gap={2}>
      <IntentIcon tier={tier} />
      <Text fontSize="sm">{score}</Text>
    </Flex>
  );
};

const _IntentTierRendererWithPopover = ({
  tier,
  score,
  enrichedFields,
  fields,
}: {
  tier?: 'high' | 'medium' | 'low';
  score?: number;
  enrichedFields?: EnrichedFields;
  fields: AccountFields;
}) => {
  const {intentFields, intentModel} = useIntentFields({
    enrichedFields,
    accountFields: fields,
  });

  // If no intent model or no score, just render the basic tier
  if (!intentModel || !score) {
    return <IntentTierRenderer tier={tier} score={score} />;
  }

  return (
    <Popover isLazy={true} placement="end" trigger="hover">
      <PopoverTrigger>
        <Box>
          <IntentTierRenderer tier={tier} score={score} />
        </Box>
      </PopoverTrigger>
      <PopoverContent p={2}>
        <PopoverArrow />
        <PopoverHeader border={0} fontWeight={500}>
          Intent Score Details
        </PopoverHeader>
        <PopoverBody>
          <Flex direction={'column'} gap={4}>
            <VStack align="start" spacing={'0'}>
              <Box color="kgray.300" fontSize={'xs'} textTransform="uppercase">
                Weighted Score
              </Box>
              <Box fontSize={'sm'}>{score}</Box>
            </VStack>
            {intentFields.map((field, idx) => (
              <VStack align="start" key={idx} spacing={'0'}>
                <Box
                  color="kgray.300"
                  fontSize={'xs'}
                  textTransform="uppercase"
                >
                  {field.label}
                </Box>
                <HStack spacing={3}>
                  <Box fontSize={'sm'}>{field.value}</Box>
                  {
                    <Text fontSize={'sm'} color="kgray.300">
                      {field.points} points
                    </Text>
                  }
                </HStack>
              </VStack>
            ))}
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const IntentTierRendererWithPopover = React.memo(
  _IntentTierRendererWithPopover
);
